import { decode, expand } from 'pluscodes';

/** Abeceda plus kódu - dvadsať znakov, bez tých, ktoré sa dajú zameniť. */
const CHAR = '[23456789CFGHJMPQRVWX]';

/**
 * Úplný globálny kód: osem znakov, `+`, dva az sedem znakov za ním. Je
 * absolútny, referenčnú polohu nepotrebuje.
 */
const GLOBAL_CODE = new RegExp(`^${CHAR}{8}\\+${CHAR}{2,7}$`, 'i');

/**
 * Skrátený kód - z globálneho sú odrezané dva, štyri alebo šesť úvodných
 * znakov. Sám o sebe polohu neurčuje: platí len v okolí známeho miesta, takže
 * sa dekóduje výhradne s referenciou.
 */
const SHORT_CODE = new RegExp(
  `^(?:${CHAR}{2}|${CHAR}{4}|${CHAR}{6})\\+${CHAR}{2,7}$`,
  'i',
);

/**
 * Text rozobraný na kód a na to, čo prípadne stojí za ním (mesto v zloženom
 * tvare „9G8F+6W Prešov").
 *
 * Bez `+` je jediný povolený tvar ten, kde ho používateľ nahradil medzerou -
 * z mobilnej klávesnice sa to stáva. So `+` sa medzery nenahrádzajú, inak by sa
 * z mesta za kódom stala jeho súčasť.
 */
const parseParts = (input: string): { code: string; rest: string } => {
  const trimmed = input.trim();

  if (!trimmed.includes('+')) {
    return { code: trimmed.replace(/\s+/, '+'), rest: '' };
  }

  const [code, ...rest] = trimmed.split(/\s+/);
  return { code: code ?? '', rest: rest.join(' ') };
};

/**
 * `decode` vracia pri nezmysloch predvolenú polohu (Londýn) namiesto chyby,
 * takže sa jeho výsledok musí overiť, nie prebrať.
 */
const decodeGlobal = (code: string): [number, number] | undefined => {
  try {
    const decoded = decode(code);
    if (!decoded?.latitude || !decoded?.longitude) {
      return undefined;
    }
    return [decoded.longitude, decoded.latitude];
  } catch {
    return undefined;
  }
};

/**
 * Plus kód v ktoromkoľvek povolenom tvare preložený na `[lng, lat]`.
 *
 * Formát nestačí overiť jedným regulárnym výrazom, lebo tvarov je viac a každý
 * má inú podmienku:
 *
 * - **úplný globálny** (`8FVC9G8F+6W`) - absolútny; text za ním („Prešov") je
 *   nadbytočný a ignoruje sa;
 * - **skrátený** (`9G8F+6W`) - potrebuje `reference`, inak sa nedekóduje.
 *   S dopísaným mestom sa nedekóduje **ani s referenciou**: „9G8F+6W
 *   Bratislava" expandovaný proti Prešovu je tichá chyba a to mesto v texte je
 *   jediné, čo ju vie zachytiť - nech ho teda dostane hľadanie miesta.
 *
 * Kým funkcia poznala len prvý tvar, padal zvyšok do geokódera, ten na
 * „9G8F+6W" vrátil hocijakú slovenskú obec a mapa odletela mimo - presne to
 * hlásili používatelia ako „zadal som plus kód a hodilo ma to inde".
 *
 * @param reference `[lng, lat]`, podľa ktorej sa dopĺňa skrátený kód - typicky
 *   stred mapy. Bez nej skrátené tvary nie sú platné.
 */
export const plusCodeToCoordinates = (
  input: string,
  reference?: [number, number],
): [number, number] | undefined => {
  const { code, rest } = parseParts(input);

  if (GLOBAL_CODE.test(code)) {
    return decodeGlobal(code);
  }

  if (SHORT_CODE.test(code) && !rest && reference) {
    const full = expand(code, {
      longitude: reference[0],
      latitude: reference[1],
    });
    return full ? decodeGlobal(full) : undefined;
  }

  return undefined;
};

export const isValidPlusCode = (
  code: string,
  reference?: [number, number],
): boolean => !!plusCodeToCoordinates(code, reference);
